import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { CUSTOMER_OPTIONS } from '@constants/customerOptions';

import { makePickSavingField } from '@helpers/makePickSavingField';

import { apiClient } from '@services/apiClient';

interface IResponse {
  options: [
    {
      customerId: number;
      type: number;
      status: number;
    },
    {
      customerId: number;
      type: number;
      status: number;
    },
  ];
  downtimeAvoided: number;
  lphAvoided: number;
  countReports: number;
  costAvoided: number;
  costAvoidedCustomerCurrency: number;
  costAvoidedFacilityCurrency: number;
  equipmentLaborSaving: number;
  equipmentLaborSavingCustomerCurrency: number;
  equipmentLaborSavingFacilityCurrency: number;
  singleCostAvoided: number;
  singleCostAvoidedCustomerCurrency: number;
  singleCostAvoidedFacilityCurrency: number;
}

export const customerInfoKey = (customerId?: string | number, locationIds?: Array<number>) => [
  'customerInfo',
  customerId,
  locationIds,
];

export const customerInfoFn = ({ queryKey }: QueryFunctionContext<string[]>) =>
  apiClient.get(`${APP_API_URL_V2}fsd/customer/${queryKey[1]}/info`, {
    params: {
      location_ids: queryKey[2],
    },
  });

const customerInfoSelect =
  (locationIds?: Array<number>) =>
  ({ data }: AxiosResponse<IResponse>) => {
    const customerOptions: { [key: number]: number } = {};

    const pick = makePickSavingField(!locationIds);

    if (data?.options) {
      data?.options.forEach((option) => (customerOptions[+option.type] = +option.status));
    }

    const {
      equipmentLaborSavingCustomerCurrency,
      equipmentLaborSavingFacilityCurrency,
      costAvoidedCustomerCurrency,
      costAvoidedFacilityCurrency,
      singleCostAvoidedCustomerCurrency,
      singleCostAvoidedFacilityCurrency,
      ...rest
    } = data;

    return {
      ...rest,
      showTotalCostSavings: !!customerOptions[CUSTOMER_OPTIONS.TCS],
      showUnplannedDowntime: !!customerOptions[CUSTOMER_OPTIONS.DTA],
      showCompletionReports: !!customerOptions[CUSTOMER_OPTIONS.TCR],
      showLphAvoided: !!customerOptions[CUSTOMER_OPTIONS.LPH],
      equipmentLaborSaving: pick(equipmentLaborSavingCustomerCurrency, equipmentLaborSavingFacilityCurrency),
      costAvoided: pick(costAvoidedCustomerCurrency, costAvoidedFacilityCurrency),
      singleCostAvoided: pick(singleCostAvoidedCustomerCurrency, singleCostAvoidedFacilityCurrency),
    };
  };

export const useCustomerInfoQuery = ({
  customerId,
  locationIds,
}: {
  customerId?: string | number;
  locationIds?: Array<number>;
}) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: customerInfoKey(customerId, locationIds),
    queryFn: customerInfoFn,
    enabled: !!customerId && customerId !== 'undefined',
    select: customerInfoSelect(locationIds),
  });
};
